// ============================================================================
// BOOKING PORTALS · MASTER REGISTRY
// ============================================================================
// One config file drives four distinct booking portals:
//   /book           → STAY portal (destinations → hotels)
//   /book/yatra     → YATRA portal (sections → rich detail view + enquiry)
//   /book/wedding   → WEDDING portal (venues → collections)
//   /book/event     → EVENT portal (event types → formats)
//
// PORTAL MODES:
//   mode: "options"  → 3-step flow (grid → option list → form)     (default)
//   mode: "detail"   → 2-step flow (grid → detail view + form)
//
// SECTIONS (optional):
//   When portal.sections is present, the grid groups cards by section.
//   Cards' `section` field points to a section key.
//
// Add a package: append to `categories`.
// Add an option: append to `categories[].options`.
// ============================================================================

import { DESTINATIONS } from "./destinations";

const HERO_STAY      = "https://images.pexels.com/photos/14149541/pexels-photo-14149541.jpeg?auto=compress&cs=tinysrgb&w=2000&q=80";
const HERO_YATRA     = "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&w=2000&q=80";
const HERO_WEDDING   = "https://images.unsplash.com/photo-1587271636175-90d58cdad458?auto=format&fit=crop&w=2000&q=80";
const HERO_EVENT     = "https://images.unsplash.com/photo-1544198365-f5d60b6d8190?auto=format&fit=crop&w=2000&q=80";
const HERO_TRANSPORT = "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=2000&q=80";

// Authentic destination imagery — verified Wikimedia Commons + high-quality Pexels/Unsplash URLs
// Each yatra card gets a UNIQUE, subject-matching photograph
const IMG = {
  // Char Dham temples — high-quality unique photos (Unsplash/Pexels for reliability)
  kedarnathTemple:  "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&w=1600&q=85",
  kedarnathRainy:   "https://images.pexels.com/photos/14149541/pexels-photo-14149541.jpeg?auto=compress&cs=tinysrgb&w=1600&q=85",
  kedarnathMorning: "/केदारनाथ.png u",
  badrinathTemple:  "/बद्रीनाथ.png ",
  badrinathDevotees:"https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=1600&q=85",
  gangotriRiver:    "/गंगोत्री.png",
  yamunotriTemple:  "/यमुनोत्री.png",

  // Combined pilgrimage packages — Pexels/Unsplash high-quality
  himalayaDawn:     "https://images.pexels.com/photos/14149541/pexels-photo-14149541.jpeg?auto=compress&cs=tinysrgb&w=1600&q=85",
  himalayaTemple:   "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&w=1600&q=85",
  hemkundLake:      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=85",
  alpineBloom:      "https://images.unsplash.com/photo-14651463444U-f00d5f5c8f07?auto=format&fit=crop&w=1600&q=85",

  // Uttarakhand tours
  valleyFlowers:    "https://images.unsplash.com/photo-1500534623283-312aade485b7?auto=format&fit=crop&w=1600&q=85",
  choptaMeadow:     "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&w=1600&q=85",
  auliSnow:         "https://images.unsplash.com/photo-1551524559-8af4e6624178?auto=format&fit=crop&w=1600&q=85",
  mussoorieHills:   "https://images.unsplash.com/photo-1587922546925-1b1e6a06d5e5?auto=format&fit=crop&w=1600&q=85",
  rishikeshGanga:   "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1600&q=85",
  haridwarAarti:    "https://images.unsplash.com/photo-1560195587-4a9256134f68?auto=format&fit=crop&w=1600&q=85",
  doonValley:       "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1600&q=85",
  uttarakhandMap:   "https://images.unsplash.com/photo-1544198365-f5d60b6d8190?auto=format&fit=crop&w=1600&q=85",

  // Wedding / event reuse
  mandap:           "https://images.unsplash.com/photo-1587271636175-90d58cdad458?auto=format&fit=crop&w=1600&q=85",

  // Backwards-compatibility aliases used by wedding/event portals
  kedarnath:   "https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Kedarnath_Temple.jpg/1600px-Kedarnath_Temple.jpg",
  himalayas:   "https://images.pexels.com/photos/14149541/pexels-photo-14149541.jpeg?auto=compress&cs=tinysrgb&w=1600&q=85",
  greenValley: "https://images.unsplash.com/photo-1544198365-f5d60b6d8190?auto=format&fit=crop&w=1600&q=85",
  meadowBloom: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?auto=format&fit=crop&w=1600&q=85",
  pineForest:  "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&w=1600&q=85",
  rishikesh:   "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1600&q=85",
};

// ---------------------- STAY (destinations → hotels) ------------------------
const STAY = {
  key: "stay",
  slug: "/book",
  mode: "options",
  enquiryType: "stay",
  heroImage: HERO_STAY,
  overline: "Smart Booking Portal",
  step1Title: "Where would you like",
  step1TitleAccent: "to journey?",
  step1Sub: "Choose a destination in the Himalayan foothills or Char Dham circuit. We handle the stay, the pandit, the pass, the taxi and the tea. Your journey begins with a single click.",
  step2Label: "Select Your Stay",
  step3Label: "Enquiry",
  step1CrumbLabel: "Destinations",
  step2ItemName: "stay option",
  step2SelectedIntroLabel: "Property",
  step3IntroTitle: "Almost there.",
  step3IntroSub: "Share a few details.",
  submitLabel: "Submit Enquiry · Open WhatsApp",
  categories: DESTINATIONS.map(d => ({
    id: d.id,
    name: d.name,
    subtitle: d.subtitle,
    tagline: d.tagline,
    icon: d.icon,
    image: d.image,
    options: d.hotels.map(h => ({ ...h, category: "property" })),
  })),
  fields: [
    { k: "check_in", label: "Check-in", type: "date", col: 1 },
    { k: "check_out", label: "Check-out", type: "date", col: 1 },
    { k: "guests", label: "Guests", type: "text", placeholder: "e.g. 2", col: 1 },
    { k: "room_type", label: "Room Preference", type: "select", options: ["Premium Couple", "Premium Family", "Premium Family Suite", "Any recommendation"], col: 1 },
    { k: "message", label: "Notes · Optional", type: "textarea", placeholder: "e.g. helicopter yatra plans, family with elders, etc.", col: 2 },
  ],
};

// ---------------------- YATRA (sections → detail view) ---------------------
const YATRA = {
  key: "yatra",
  slug: "/book/yatra",
  mode: "detail",
  enquiryType: "chardham",
  heroImage: HERO_YATRA,
  overline: "Sacred Yatra Portal",
  step1Title: "Which dham calls",
  step1TitleAccent: "your heart?",
  step1Sub: "From the abode of Mahadev at Kedarnath to the sacred four dhams, we handle every step — permits, helicopters, taxis, pandits, and warm meals along the road. Choose your journey below.",
  step2Label: "Package Details & Enquiry",
  step3Label: "Enquiry",
  step1CrumbLabel: "Yatras",
  step2ItemName: "package",
  step2SelectedIntroLabel: "Package",
  step3IntroTitle: "Let us plan.",
  step3IntroSub: "Share your travel dates and family size.",
  submitLabel: "Plan My Yatra · Open WhatsApp",
  sections: [
    { key: "featured", label: "Featured Char Dham Pilgrimages",  sub: "The four sacred abodes — with full integrated itineraries." },
    { key: "packages", label: "Popular Pilgrimage Packages",     sub: "Combined dhams for those with sacred distance to cover." },
    { key: "tours",    label: "Popular Uttarakhand Tours",       sub: "The Himalayas beyond pilgrimage — meadows, valleys, hill stations." },
  ],
  categories: [
    // ============ FEATURED CHAR DHAM ============
    {
      id: "kedarnath-integrated",
      section: "featured",
      name: "Kedarnath",
      subtitle: "Integrated Yatra Package",
      tagline: "3,583 m · The abode of Mahadev",
      icon: "temple",
      image: IMG.kedarnathTemple,
      duration: "6 nights · 7 days",
      helicopter: "Available from Sersi · Phata · Guptkashi (weather permitting)",
      includes: ["Haridwar", "Rishikesh", "Devprayag", "Rudraprayag", "Guptkashi", "Phata", "Sersi", "Sonprayag", "Gaurikund", "Kedarnath Temple", "Triyuginarayan Temple", "Chopta", "Tungnath", "Chandrashila"],
      highlights: [
        "Darshan at the sacred Jyotirlinga of Kedarnath",
        "Two nights at Kedarnath Cottage, Sersi — our signature property",
        "Sacred detour to Triyuginarayan Temple (where Shiva & Parvati wed)",
        "Optional summit trek to Chandrashila at 4,000 m",
        "Ganga aarti at Har Ki Pauri, Haridwar on arrival",
      ],
      hotels: ["Kedarnath Cottage · Sersi (Signature)", "Boutique hotels · Rishikesh & Rudraprayag", "Luxury stays · Guptkashi"],
      vehicles: ["Innova Crysta (up to 6)", "Tempo Traveller (up to 12)", "Ertiga (compact · up to 4)"],
    },
    {
      id: "badrinath-integrated",
      section: "featured",
      name: "Badrinath",
      subtitle: "Integrated Yatra Package",
      tagline: "3,133 m · The abode of Lord Vishnu",
      icon: "temple",
      image: IMG.himalayas,
      duration: "5 nights · 6 days",
      helicopter: "Not available for Badrinath directly · Sahastradhara heli to Dehradun",
      includes: ["Joshimath", "Pandukeshwar", "Mana Village (India's last village)", "Vyas Gufa", "Ganesh Gufa", "Bhim Pul", "Saraswati River", "Vasudhara Falls", "Auli (Optional)"],
      highlights: [
        "Darshan at the sacred Badrinath temple",
        "Walk to Mana — the last village on India's Tibetan border",
        "Sacred caves of Vyas and Ganesh where the Mahabharata was composed",
        "Optional ropeway ride at Auli · Asia's second longest",
        "Bhim Pul over the roaring Saraswati river",
      ],
      hotels: ["Boutique hotels · Joshimath & Pandukeshwar", "Alpine stays · Auli (optional)"],
      vehicles: ["Innova Crysta", "Tempo Traveller (groups)", "SUV (for high-altitude comfort)"],
    },
    {
      id: "gangotri-integrated",
      section: "featured",
      name: "Gangotri",
      subtitle: "Integrated Yatra Package",
      tagline: "3,100 m · Where the Ganga descends",
      icon: "temple",
      image: IMG.gangotriRiver,
      duration: "4 nights · 5 days",
      helicopter: "Not available · Road-only journey via Uttarkashi",
      includes: ["Uttarkashi", "Harsil", "Dharali", "Mukhba Village (Winter Abode of Ganga)", "Gangotri Temple"],
      highlights: [
        "Darshan at Gangotri temple — the origin of the sacred Ganga",
        "Overnight in Harsil valley — Uttarakhand's apple country",
        "Visit Mukhba, the winter home of the Ganga idol",
        "Riverside meditation by the Bhagirathi",
        "Excellent photography in the Dharali–Harsil belt",
      ],
      hotels: ["Boutique cottages · Harsil valley", "Comfortable stays · Uttarkashi", "Riverside camps · Dharali"],
      vehicles: ["Innova (recommended for hill roads)", "Tempo Traveller (groups up to 12)"],
    },
    {
      id: "yamunotri-integrated",
      section: "featured",
      name: "Yamunotri",
      subtitle: "Integrated Yatra Package",
      tagline: "3,293 m · The source of the Yamuna",
      icon: "temple",
      image: IMG.yamunotriTemple,
      duration: "3 nights · 4 days",
      helicopter: "Available from Kharsali (near Janki Chatti)",
      includes: ["Barkot", "Hanuman Chatti", "Janki Chatti", "Yamunotri Temple", "Surya Kund (hot spring)", "Divya Shila"],
      highlights: [
        "Darshan at Yamunotri temple — the origin of the Yamuna",
        "Cook rice in the sacred Surya Kund hot spring — a rare offering",
        "6 km trek / pony ride from Janki Chatti to the temple",
        "Overnight stay in the pine-scented town of Barkot",
        "Prostrate before the sacred Divya Shila (divine rock)",
      ],
      hotels: ["Comfortable stays · Barkot", "Riverside lodges · Janki Chatti"],
      vehicles: ["Innova (recommended)", "Tempo Traveller (up to 12)"],
    },

    // ============ POPULAR PILGRIMAGE PACKAGES ============
    {
      id: "do-dham-ked-bad",
      section: "packages",
      name: "Do Dham · Kedarnath + Badrinath",
      subtitle: "The two great abodes",
      tagline: "Shiva & Vishnu · 8 nights · 9 days",
      icon: "temple",
      image: IMG.kedarnathRainy,
      duration: "8 nights · 9 days",
      helicopter: "Available for Kedarnath (Sersi / Phata heli)",
      includes: ["Haridwar", "Rishikesh", "Guptkashi", "Sersi", "Kedarnath", "Joshimath", "Badrinath", "Mana Village", "Auli (Optional)"],
      highlights: [
        "Two of India's holiest dhams in one journey",
        "Two nights at Kedarnath Cottage, Sersi",
        "Optional Kedarnath helicopter yatra",
        "Cave visits at Mana Village",
        "Ganga aarti evening at Haridwar",
      ],
      hotels: ["Kedarnath Cottage · Sersi", "Boutique stays · Joshimath, Guptkashi, Rishikesh"],
      vehicles: ["Innova Crysta", "Tempo Traveller (groups)"],
    },
    {
      id: "do-dham-gan-yam",
      section: "packages",
      name: "Do Dham · Gangotri + Yamunotri",
      subtitle: "The sister sacred sources",
      tagline: "Ganga & Yamuna · 6 nights · 7 days",
      icon: "temple",
      image: IMG.uttarakhandMap,
      duration: "6 nights · 7 days",
      helicopter: "Available for Yamunotri (Kharsali heli)",
      includes: ["Dehradun", "Barkot", "Janki Chatti", "Yamunotri Temple", "Uttarkashi", "Harsil", "Dharali", "Gangotri Temple"],
      highlights: [
        "Two sister dhams — the sources of India's holiest rivers",
        "Optional Yamunotri helicopter package",
        "Overnight in the picturesque Harsil valley",
        "Cook rice in the divine Surya Kund",
        "Photography paradise: Dharali, Harsil, Mukhba",
      ],
      hotels: ["Boutique cottages · Harsil", "Comfortable stays · Barkot, Uttarkashi"],
      vehicles: ["Innova Crysta", "Tempo Traveller (groups)"],
    },
    {
      id: "char-dham",
      section: "packages",
      name: "Char Dham Yatra",
      subtitle: "The four sacred abodes",
      tagline: "Yamunotri · Gangotri · Kedarnath · Badrinath",
      icon: "temple",
      image: IMG.kedarnathMorning,
      duration: "11 nights · 12 days",
      helicopter: "5-day full Char Dham heli package available",
      includes: ["Haridwar", "Barkot", "Yamunotri", "Uttarkashi", "Harsil", "Gangotri", "Guptkashi", "Sersi", "Kedarnath", "Joshimath", "Badrinath", "Mana Village", "Rishikesh"],
      highlights: [
        "Complete Char Dham circuit in one integrated journey",
        "Two nights at Kedarnath Cottage, Sersi",
        "Optional 5-day helicopter Char Dham package",
        "Ganga aarti at both Haridwar and Rishikesh",
        "Personal wedding-style hospitality throughout",
      ],
      hotels: ["Kedarnath Cottage · Sersi", "Curated hotels across all 4 dhams", "Airport pickup from Dehradun"],
      vehicles: ["Innova Crysta (families)", "Tempo Traveller (up to 12)", "Bus (large groups)"],
    },
    {
      id: "hemkund-sahib",
      section: "packages",
      name: "Hemkund Sahib Yatra",
      subtitle: "The sacred glacial lake",
      tagline: "4,632 m · Sikh pilgrimage",
      icon: "flower",
      image: IMG.hemkundLake,
      duration: "5 nights · 6 days",
      helicopter: "Not available · Trek only from Govindghat via Ghangaria",
      includes: ["Haridwar", "Rishikesh", "Joshimath", "Govindghat", "Ghangaria", "Hemkund Sahib Gurudwara"],
      highlights: [
        "Sacred darshan at Hemkund Gurudwara by the glacial lake",
        "13 km trek from Govindghat to Ghangaria (with option for pony)",
        "Overnight in the alpine town of Ghangaria",
        "Optional add-on to Valley of Flowers",
        "Path taken by Guru Gobind Singh Ji himself",
      ],
      hotels: ["Boutique stays · Joshimath", "Trekker lodges · Ghangaria", "Riverside stays · Rishikesh"],
      vehicles: ["Innova (recommended)", "Tempo Traveller (groups)"],
    },
    {
      id: "hemkund-vof",
      section: "packages",
      name: "Hemkund Sahib + Valley of Flowers",
      subtitle: "Sacred lake meets alpine bloom",
      tagline: "UNESCO valley · Peak bloom: Jul–Aug",
      icon: "flower",
      image: IMG.alpineBloom,
      duration: "6 nights · 7 days",
      helicopter: "Not available · Both are trek destinations",
      includes: ["Haridwar", "Rishikesh", "Joshimath", "Govindghat", "Ghangaria", "Valley of Flowers", "Hemkund Sahib"],
      highlights: [
        "Two of Uttarakhand's most spectacular alpine destinations",
        "UNESCO-listed Valley of Flowers in peak bloom",
        "Sacred Hemkund lake at 4,632 m",
        "Two nights in the alpine town of Ghangaria",
        "Best experienced July–August for full flower bloom",
      ],
      hotels: ["Boutique stays · Joshimath", "Alpine lodges · Ghangaria"],
      vehicles: ["Innova Crysta", "Tempo Traveller (groups)"],
    },

    // ============ POPULAR UTTARAKHAND TOURS ============
    {
      id: "valley-of-flowers",
      section: "tours",
      name: "Valley of Flowers",
      subtitle: "UNESCO alpine bloom",
      tagline: "Peak: Jul–Aug · 3 km trek",
      icon: "flower",
      image: IMG.valleyFlowers,
      duration: "4 nights · 5 days",
      helicopter: "Not available",
      includes: ["Rishikesh", "Joshimath", "Govindghat", "Ghangaria", "Valley of Flowers"],
      highlights: [
        "300+ species of alpine flowers in full bloom",
        "UNESCO World Heritage site",
        "Overnight at the base camp of Ghangaria",
        "3 km moderate trek from Ghangaria to the valley",
        "Optional add-on to Hemkund Sahib",
      ],
      hotels: ["Boutique stays · Joshimath", "Alpine lodges · Ghangaria"],
      vehicles: ["Innova Crysta", "Tempo Traveller"],
    },
    {
      id: "chopta-tungnath-chandrashila",
      section: "tours",
      name: "Chopta · Tungnath · Chandrashila",
      subtitle: "The mini Switzerland of India",
      tagline: "Highest Shiva temple · Sunrise summit",
      icon: "mountain",
      image: IMG.choptaMeadow,
      duration: "3 nights · 4 days",
      helicopter: "Not available",
      includes: ["Rudraprayag", "Chopta", "Tungnath Temple (3,680 m)", "Chandrashila Summit (4,000 m)", "Deoria Tal (optional)"],
      highlights: [
        "Tungnath — the highest Shiva temple in the world",
        "Chandrashila summit trek — 360° Himalayan panorama",
        "Chopta meadows — Uttarakhand's mini Switzerland",
        "Perfect for photographers and moderate trekkers",
        "Optional visit to the pristine Deoria Tal lake",
      ],
      hotels: ["Alpine camps · Chopta", "Boutique stays · Rudraprayag"],
      vehicles: ["Innova (recommended)", "SUV (high-altitude)"],
    },
    {
      id: "auli-tour",
      section: "tours",
      name: "Auli Tour",
      subtitle: "Snow slopes & ropeway",
      tagline: "Asia's second longest ropeway",
      icon: "mountain",
      image: IMG.auliSnow,
      duration: "4 nights · 5 days",
      helicopter: "Not available",
      includes: ["Rishikesh", "Joshimath", "Auli (Nanda Devi views)", "Gorson Bugyal", "Chattrakund"],
      highlights: [
        "Nanda Devi panorama — India's second highest peak",
        "Ride Asia's second-longest ropeway (Auli–Joshimath)",
        "Ski slopes in winter, alpine meadows in summer",
        "Overnight at Auli's peaceful alpine resort",
        "Trek to Gorson Bugyal for the ultimate meadow view",
      ],
      hotels: ["Ski resorts · Auli", "Boutique stays · Joshimath"],
      vehicles: ["Innova Crysta", "Tempo Traveller (groups)"],
    },
    {
      id: "mussoorie-tour",
      section: "tours",
      name: "Mussoorie Tour",
      subtitle: "Queen of the Hills",
      tagline: "2,005 m · Mall Road · Colonial charm",
      icon: "mountain",
      image: IMG.mussoorieHills,
      duration: "3 nights · 4 days",
      helicopter: "Not available",
      includes: ["Dehradun", "Mussoorie (Mall Road)", "Kempty Falls", "Gun Hill", "Library Chowk", "Landour"],
      highlights: [
        "Britain's favourite Himalayan hill station",
        "Walk on the historic Mall Road",
        "Cable car ride to Gun Hill for city views",
        "Kempty Falls in the pine belt",
        "Explore Landour — Ruskin Bond's home",
      ],
      hotels: ["Colonial heritage stays · Mall Road", "Boutique cottages · Landour"],
      vehicles: ["Sedan (Mussoorie roads)", "Innova (comfortable choice)"],
    },
    {
      id: "rishikesh-haridwar",
      section: "tours",
      name: "Rishikesh & Haridwar Tour",
      subtitle: "Yoga capital & Gateway to Gods",
      tagline: "Ganga · Aarti · Adventure",
      icon: "om",
      image: IMG.rishikeshGanga,
      duration: "3 nights · 4 days",
      helicopter: "Not available",
      includes: ["Haridwar (Har Ki Pauri)", "Chandi Devi Temple", "Rishikesh (Ram Jhula, Laxman Jhula)", "Triveni Ghat", "Beatles Ashram", "Shivpuri"],
      highlights: [
        "Evening Ganga aarti at Har Ki Pauri, Haridwar",
        "Iconic Ram Jhula & Laxman Jhula walks",
        "Beatles Ashram — where Sgt Pepper was written",
        "Optional white water rafting from Shivpuri",
        "Yoga session at a traditional ashram",
      ],
      hotels: ["Boutique riverside stays · Rishikesh", "Comfort hotels · Haridwar"],
      vehicles: ["Sedan (city tours)", "Innova (comfortable)"],
    },
    {
      id: "dehradun-mussoorie",
      section: "tours",
      name: "Dehradun & Mussoorie Tour",
      subtitle: "Doon Valley + Queen of Hills",
      tagline: "Robber's Cave · Mall Road · Landour",
      icon: "mountain",
      image: IMG.doonValley,
      duration: "4 nights · 5 days",
      helicopter: "Not available",
      includes: ["Dehradun (Robber's Cave, Sahastradhara)", "Mindrolling Monastery", "Mussoorie (Mall Road)", "Kempty Falls", "Landour", "Gun Hill"],
      highlights: [
        "Explore Dehradun — Uttarakhand's serene capital",
        "Robber's Cave — nature's underground stream walk",
        "Mindrolling Monastery — the largest stupa in India",
        "Full day in Mussoorie · Mall Road & Kempty",
        "Landour village exploration",
      ],
      hotels: ["Boutique stays · Dehradun", "Heritage stays · Mussoorie Mall Road"],
      vehicles: ["Sedan or Innova (both regions)"],
    },
    {
      id: "custom-uttarakhand",
      section: "tours",
      name: "Customized Uttarakhand Tour",
      subtitle: "Design your own itinerary",
      tagline: "Any dham · Any tour · Any duration",
      icon: "mountain",
      image: IMG.haridwarAarti,
      duration: "Custom",
      helicopter: "On request · dependent on route",
      includes: ["Any combination of dhams, hill stations, wildlife, adventure and pilgrimage sites"],
      highlights: [
        "Every itinerary hand-crafted by our travel director",
        "Perfect for families with elderly, NRI groups, corporate groups",
        "Add wildlife (Jim Corbett), adventure (rafting), spirituality (yoga)",
        "Wedding + pilgrimage combos possible",
        "Airport pickup and drop from Dehradun/Delhi included",
      ],
      hotels: ["Curated by preference — boutique, luxury or heritage"],
      vehicles: ["Any — sedan, SUV, Innova, Tempo, Bus, Traveller"],
    },
  ],
  fields: [
    { k: "travel_month", label: "Travel Month", type: "text", placeholder: "e.g. May 2026", col: 1 },
    { k: "guests", label: "Number of Guests", type: "text", placeholder: "e.g. 4", col: 1 },
    { k: "message", label: "Requirements · Optional", type: "textarea", placeholder: "e.g. elderly parents, helicopter preference, dietary needs", col: 2 },
  ],
};

// ---------------------- WEDDING (venues → collections) ---------------------
const WEDDING = {
  key: "wedding",
  slug: "/book/wedding",
  mode: "options",
  enquiryType: "wedding",
  heroImage: HERO_WEDDING,
  overline: "Divine Wedding Portal",
  step1Title: "Where will you say",
  step1TitleAccent: "forever?",
  step1Sub: "The Himalayas hold many sacred sites for a wedding. Choose your venue — from the origin of all weddings at Triyuginarayan Temple, to a riverside mandap beneath the peaks.",
  step2Label: "Choose Your Collection",
  step3Label: "Enquiry",
  step1CrumbLabel: "Venues",
  step2ItemName: "collection",
  step2SelectedIntroLabel: "Collection",
  step3IntroTitle: "A hello begins.",
  step3IntroSub: "Share your wedding dates and family plans.",
  submitLabel: "Plan My Wedding · Open WhatsApp",
  categories: [
    {
      id: "triyuginarayan", name: "Triyuginarayan Temple",
      subtitle: "Where Shiva and Parvati wed",
      tagline: "The eternal Akhand Dhuni · Sacred pheras",
      icon: "temple", image: IMG.mandap,
      options: [
        { id: "tri-sacred-vow",         name: "Sacred Vow · Intimate (up to 25)",       note: "2 nights · pheras + boutique stay" },
        { id: "tri-divine-union",       name: "Divine Union · Signature (up to 60)",    note: "3 nights · full temple ceremony + reception", featured: true },
        { id: "tri-celestial-blessing", name: "Celestial Blessing · Grand (up to 120)", note: "4 nights · buyout + helicopter arrival" },
      ],
    },
    {
      id: "kc-riverside", name: "Kedarnath Cottage Riverside, Sersi",
      subtitle: "By the Mandakini river",
      tagline: "Boutique cottage · alpine setting",
      icon: "mountain", image: IMG.kedarnath,
      options: [
        { id: "kc-intimate",  name: "Intimate Riverside (up to 30)" },
        { id: "kc-signature", name: "Signature Riverside (up to 70)", featured: true },
      ],
    },
    {
      id: "sonprayag-meadow", name: "Sonprayag Alpine Meadow",
      subtitle: "Under the open sky",
      tagline: "Where two rivers meet · dramatic backdrop",
      icon: "flower", image: IMG.pineForest,
      options: [
        { id: "son-meadow-elegant",  name: "Elegant Meadow (up to 50)" },
        { id: "son-meadow-grand",    name: "Grand Meadow (up to 150)", featured: true },
      ],
    },
    {
      id: "custom-wedding", name: "Custom Destination",
      subtitle: "You have a place in mind",
      tagline: "We arrange the venue, permissions and hospitality",
      icon: "temple", image: IMG.greenValley,
      options: [
        { id: "custom-elopement",   name: "Sacred Elopement (2 guests)" },
        { id: "custom-boutique",    name: "Boutique Celebration (up to 40)" },
        { id: "custom-grand",       name: "Grand Celebration (custom count)", featured: true },
      ],
    },
  ],
  fields: [
    { k: "wedding_month", label: "Wedding Month", type: "text", placeholder: "e.g. October 2026", col: 1 },
    { k: "expected_guests", label: "Expected Guests", type: "text", placeholder: "e.g. 60", col: 1 },
    { k: "city", label: "City", type: "text", placeholder: "Your home city", col: 2 },
    { k: "message", label: "Your Vision · Optional", type: "textarea", placeholder: "e.g. muhurat preferences, décor style, family traditions", col: 2 },
  ],
};

// ---------------------- EVENT (event types → formats) ----------------------
const EVENT = {
  key: "event",
  slug: "/book/event",
  mode: "options",
  enquiryType: "contact",
  heroImage: HERO_EVENT,
  overline: "Private Events Portal",
  step1Title: "What are we",
  step1TitleAccent: "celebrating?",
  step1Sub: "Anniversaries, family reunions, corporate retreats, sacred milestones — we host the moment with the same care we hold every guest. Choose the celebration you want to plan.",
  step2Label: "Choose Your Format",
  step3Label: "Enquiry",
  step1CrumbLabel: "Events",
  step2ItemName: "format",
  step2SelectedIntroLabel: "Format",
  step3IntroTitle: "Let's begin.",
  step3IntroSub: "Share your dates and the shape of your celebration.",
  submitLabel: "Plan My Event · Open WhatsApp",
  categories: [
    { id: "corporate-retreat", name: "Corporate Retreat", subtitle: "Team offsites in the Himalayas", tagline: "Focus · Nature · Renewal", icon: "mountain", image: IMG.pineForest,
      options: [{ id: "corp-intimate", name: "Intimate Retreat (up to 15)" }, { id: "corp-signature", name: "Signature Retreat (up to 40)", featured: true }, { id: "corp-grand", name: "Grand Retreat (up to 100)" }] },
    { id: "family-reunion", name: "Family Reunion", subtitle: "The whole family, together", tagline: "For elders · children · grandchildren", icon: "temple", image: IMG.mandap,
      options: [{ id: "fam-intimate", name: "Intimate Reunion (up to 20)" }, { id: "fam-signature", name: "Signature Reunion (up to 50)", featured: true }] },
    { id: "new-year-himalayas", name: "31st Celebration & New Year Blessings", subtitle: "3 Days · 2 Nights Premium Experience", tagline: "Celebrate Your Success · Bid Farewell to the Year · Welcome the New Year with Mahadev's Blessings", icon: "lamp", image: IMG.himalayas,
      featured: true,
      seasonalRibbon: "Limited · Dec 30 – Jan 1",
      duration: "3 Days · 2 Nights",
      route: "Rishikesh → Kedarnath Cottage · Sersi → Chopta → Rishikesh",
      description: "The year's biggest evening — held in the alpine hush of the Kedarnath valley. Bonfire under the Himalayan sky, folk music, gala dinner, midnight aarti, and the first sunrise of the new year, all wrapped in the hospitality of THE KC INDIA.",
      includes: [
        "Premium stay at Kedarnath Cottage · Sersi",
        "Traditional Uttarakhandi welcome ceremony",
        "Bonfire under the Himalayan sky",
        "Live folk music & cultural evening",
        "Grand Himalayan gala dinner",
        "31st celebration night · dance floor",
        "New Year countdown at midnight",
        "Mahadev Blessing Ceremony",
        "Midnight aarti on the Mandakini",
        "Anniversary-style cake celebration",
        "First sunrise of the new year · viewpoint drive",
        "Special Rudrabhishek for the family",
        "Temple visit · Triyuginarayan darshan option",
        "Himalayan valley sightseeing",
        "Chopta excursion · Chandrashila views",
        "Daily breakfast & dinner",
        "Complimentary welcome kit · KC signature",
        "Private transportation (selected packages)",
      ],
      whyChoose: [
        "Celebrate the year's biggest evening in the Himalayas",
        "Begin the New Year with the blessings of Mahadev",
        "Luxury hospitality with authentic spiritual experiences",
        "Perfect for couples, families, entrepreneurs, corporate groups & friends",
        "A unique blend of celebration, spirituality and Himalayan beauty",
      ],
      options: [
        { id: "ny-couples",    name: "Couple's Retreat",    note: "Two of you · Signature suite · Candlelight anniversary-style dinner · Private aarti", featured: true },
        { id: "ny-family",     name: "Family Package",       note: "Up to 8 · Two premium rooms · Family-style gala dinner · Sunrise drive included" },
        { id: "ny-friends",    name: "Friends & Founders",   note: "Up to 15 · Group buyout · Private DJ floor · Bonfire lounge · Corporate off-site friendly" },
        { id: "ny-corporate",  name: "Corporate Group",      note: "20+ · Full property buyout · Themed gala · Team wellness add-ons available" },
      ] },
    { id: "divine-anniversary", name: "Divine Anniversary Celebration", subtitle: "Signature 4-Day Journey · Rishikesh → Kedarnath → Chopta", tagline: "Renew your vows where Lord Shiva & Parvati took theirs", icon: "temple", image: IMG.mandap,
      featured: true,
      description: "Celebrate your anniversary in the divine land of Lord Shiva. Vow Renewal at Triyuginarayan Temple · Rudrabhishek at Guptkashi · Kedarnath darshan · Chopta viewpoints. Includes Vedic ceremony, \"Blessed by Mahadev\" certificate, cinematic photo/video, premium KC stay, and private premium vehicle throughout.",
      route: "Rishikesh → Guptkashi/Sersi → Triyuginarayan → Kedarnath → Chopta → Rishikesh",
      duration: "4 Days · 3 Nights",
      options: [
        { id: "divine-signature",  name: "Signature Journey · 4D/3N",       note: "Full itinerary · Vow Renewal · Kedarnath darshan · Chopta", featured: true },
        { id: "divine-extended",   name: "Extended Journey · 5D/4N",         note: "Adds Tungnath trek + Chandrashila sunrise + one extra Chopta night" },
        { id: "divine-silver",     name: "Silver Jubilee · 25 Years Special", note: "Elevated décor · celebrity chef dinner · silver garland exchange" },
        { id: "divine-golden",     name: "Golden Jubilee · 50 Years Special", note: "Royal décor · full family Ashirwad · legacy album + heritage film" },
        { id: "divine-bespoke",    name: "Bespoke Anniversary Journey",       note: "Tell us your vision · fully customised · helicopter option available" },
      ] },
    { id: "birthday", name: "Birthday Getaway", subtitle: "A meaningful milestone", tagline: "In the sacred lap of the Himalayas", icon: "lamp", image: IMG.meadowBloom,
      options: [{ id: "bday-intimate", name: "Intimate Birthday (up to 10)" }, { id: "bday-signature", name: "Signature Birthday (up to 30)", featured: true }] },
    { id: "photoshoot", name: "Photoshoot / Film Shoot", subtitle: "Cinematic Himalayan backdrops", tagline: "Pre-wedding · brand · film", icon: "mountain", image: IMG.kedarnath,
      options: [{ id: "shoot-half-day", name: "Half-Day Shoot" }, { id: "shoot-full-day", name: "Full-Day Shoot", featured: true }, { id: "shoot-multi-day", name: "Multi-Day Production" }] },
    { id: "spiritual-retreat", name: "Spiritual Retreat", subtitle: "Yoga · meditation · silence", tagline: "Guided by teachers · steeped in tradition", icon: "om", image: IMG.rishikesh,
      options: [{ id: "retreat-3day", name: "3-Day Silent Retreat" }, { id: "retreat-7day", name: "7-Day Yoga & Meditation", featured: true }, { id: "retreat-custom", name: "Custom Retreat" }] },
    { id: "custom-event", name: "Custom Celebration", subtitle: "Tell us what you dream", tagline: "We hold every detail with care", icon: "trees", image: IMG.himalayas,
      options: [{ id: "custom-any-size", name: "Any size · Any occasion" }] },
  ],
  fields: [
    { k: "check_in", label: "Preferred Date", type: "date", col: 1 },
    { k: "guests", label: "Expected Guests", type: "text", placeholder: "e.g. 20", col: 1 },
    { k: "message", label: "About the Occasion · Optional", type: "textarea", placeholder: "Tell us what you're planning and what would make it perfect", col: 2 },
  ],
};

// ---------------------- TRANSPORT (vehicles → journey types) --------------
const VEHICLE_IMG = {
  // Contextual mood photography — luxury road-trip vibes matched to vehicle class
  dzire:      "https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1600&q=85", // sedan airport transfer
  ertiga:     "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1600&q=85", // compact family car
  innova:     "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1600&q=85", // hill road SUV
  crysta:     "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1600&q=85", // premium SUV (Ford Expedition style)
  tempo:      "https://images.unsplash.com/photo-1543465077-db45d34b88a5?auto=format&fit=crop&w=1600&q=85",   // group travel vans
  urbania:    "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=1600&q=85", // luxury coach
  minibus:    "https://images.unsplash.com/photo-1464219789935-c2d9d9aba644?auto=format&fit=crop&w=1600&q=85", // vintage coach
  helicopter: "https://images.pexels.com/photos/1088854/pexels-photo-1088854.jpeg?auto=compress&cs=tinysrgb&w=1600&q=85", // aerial travel mood
};

const JOURNEY_OPTS_ROAD = [
  { id: "airport-transfer", name: "Airport Transfer", note: "Dehradun · Delhi · Rishikesh · Haridwar" },
  { id: "local-sightseeing", name: "Local Sightseeing", note: "Full-day city tour with breaks" },
  { id: "one-way-drop", name: "One-Way Drop", note: "Point-to-point drive with fixed pricing" },
  { id: "round-trip", name: "Round Trip · Multi-Day", note: "Multi-city itinerary with return", featured: true },
  { id: "yatra-package", name: "Full Yatra Package", note: "Char Dham · Do Dham · Ek Dham" },
];

const JOURNEY_OPTS_HELI = [
  { id: "heli-kedarnath", name: "Kedarnath Shuttle", note: "Sersi · Phata · Guptkashi helipads · same-day darshan" },
  { id: "heli-yamunotri", name: "Yamunotri Shuttle", note: "Kharsali helipad · avoids the 6 km trek" },
  { id: "heli-chardham",  name: "Char Dham Helicopter Package", note: "5-day full circuit · one seat = one pilgrim", featured: true },
  { id: "heli-charter",   name: "Custom Private Charter", note: "Sightseeing · VIP transfers · joyrides" },
];

const TRANSPORT = {
  key: "transport",
  slug: "/book/transport",
  mode: "options",
  enquiryType: "transport",
  heroImage: HERO_TRANSPORT,
  overline: "Premium Transport Portal",
  step1Title: "Which vehicle carries",
  step1TitleAccent: "your journey?",
  step1Sub: "From an airport transfer in a chauffeured Innova, to a 26-seater coach for a family Char Dham, to a helicopter shuttle to Kedarnath — verified drivers, GPS-tracked fleets, fixed pricing. No last-minute surprises.",
  step2Label: "Select Your Journey Type",
  step3Label: "Enquiry",
  step1CrumbLabel: "Vehicles",
  step2ItemName: "journey type",
  step2SelectedIntroLabel: "Journey",
  step3IntroTitle: "One last step.",
  step3IntroSub: "Share your route and travel dates.",
  submitLabel: "Get Custom Quote · Open WhatsApp",
  categories: [
    {
      id: "swift-dzire",
      name: "Swift Dzire",
      subtitle: "Budget Sedan · 4 seats",
      tagline: "Starting from ₹4,500 / Day",
      icon: "car",
      image: VEHICLE_IMG.dzire,
      startingFrom: "₹4,500 / Day",
      capacity: "4 passengers · 2 large bags",
      features: ["AC · music system", "Chauffeur included", "Fuel + tolls on actuals", "Best for couples & short trips"],
      options: JOURNEY_OPTS_ROAD,
    },
    {
      id: "ertiga",
      name: "Maruti Ertiga",
      subtitle: "Compact SUV · 6 seats",
      tagline: "Starting from ₹5,500 / Day",
      icon: "car",
      image: VEHICLE_IMG.ertiga,
      startingFrom: "₹5,500 / Day",
      capacity: "6 passengers · 4 bags",
      features: ["AC · music system", "Ideal for small families", "Verified chauffeur", "Fuel + tolls on actuals"],
      options: JOURNEY_OPTS_ROAD,
    },
    {
      id: "innova",
      name: "Toyota Innova",
      subtitle: "SUV · 6-7 seats",
      tagline: "Starting from ₹7,000 / Day",
      icon: "car",
      image: VEHICLE_IMG.innova,
      startingFrom: "₹7,000 / Day",
      capacity: "6 passengers · 5 bags",
      features: ["Reliable hill-friendly SUV", "AC · music system", "Trained chauffeur", "Best for Char Dham road yatras"],
      options: JOURNEY_OPTS_ROAD,
    },
    {
      id: "innova-crysta",
      name: "Innova Crysta",
      subtitle: "Premium SUV · 6-7 seats",
      tagline: "Starting from ₹8,500 / Day",
      icon: "car",
      image: VEHICLE_IMG.crysta,
      startingFrom: "₹8,500 / Day",
      capacity: "6 passengers · 5 bags",
      features: ["Captain-seat luxury interior", "Signature comfort for elderly", "Trained chauffeur", "Recommended for hill roads"],
      options: JOURNEY_OPTS_ROAD,
    },
    {
      id: "tempo-traveller",
      name: "Tempo Traveller",
      subtitle: "Group Van · 12 seats",
      tagline: "Starting from ₹9,500 / Day",
      icon: "van",
      image: VEHICLE_IMG.tempo,
      startingFrom: "₹9,500 / Day",
      capacity: "10-12 passengers · 12 bags",
      features: ["2×1 or 2×2 seating", "AC · pushback seats", "Group-friendly luggage bay", "Ideal for extended families"],
      options: JOURNEY_OPTS_ROAD,
    },
    {
      id: "force-urbania",
      name: "Force Urbania",
      subtitle: "Luxury Van · 13 seats",
      tagline: "Starting from ₹12,500 / Day",
      icon: "van",
      image: VEHICLE_IMG.urbania,
      startingFrom: "₹12,500 / Day",
      capacity: "12-13 passengers · plush cabin",
      features: ["Premium recliner seats", "Ambient lighting · privacy curtains", "Best luxury group travel", "Recommended for NRI groups"],
      options: JOURNEY_OPTS_ROAD,
    },
    {
      id: "mini-bus",
      name: "Mini Bus / Coach",
      subtitle: "Group Coach · 26-35 seats",
      tagline: "Starting from ₹16,000 / Day",
      icon: "van",
      image: VEHICLE_IMG.minibus,
      startingFrom: "₹16,000 / Day",
      capacity: "26 or 35 passengers · large luggage bay",
      features: ["AC coach with pushback seats", "Perfect for large groups & office trips", "Two-driver option for long routes", "Corporate & wedding baraat ready"],
      options: JOURNEY_OPTS_ROAD,
    },
    {
      id: "helicopter",
      name: "Helicopter Assistance",
      subtitle: "Aerial Yatra · 5-6 seats",
      tagline: "Price on Request",
      icon: "plane",
      image: VEHICLE_IMG.helicopter,
      startingFrom: "Price on Request",
      isHelicopter: true,
      capacity: "5-6 pilgrims per sortie",
      features: ["Kedarnath · Yamunotri · Char Dham circuits", "IRCTC-authorised operators only", "Same-day darshan possible", "Subject to weather & slot availability"],
      options: JOURNEY_OPTS_HELI,
    },
  ],
  fields: [
    { k: "pickup_location", label: "Pickup Location", type: "text", placeholder: "e.g. Dehradun Airport", col: 1 },
    { k: "drop_location",   label: "Drop / Destination", type: "text", placeholder: "e.g. Kedarnath via Guptkashi", col: 1 },
    { k: "check_in",        label: "Journey Date", type: "date", col: 1 },
    { k: "check_out",       label: "Return Date · Optional", type: "date", col: 1 },
    { k: "guests",          label: "Passengers", type: "text", placeholder: "e.g. 4 adults, 1 child", col: 1 },
    { k: "message",         label: "Notes · Optional", type: "textarea", placeholder: "e.g. elderly parents, extra luggage, sightseeing stops", col: 2 },
  ],
};

export const PORTALS = { stay: STAY, yatra: YATRA, wedding: WEDDING, event: EVENT, transport: TRANSPORT };
export const getPortal = (key) => PORTALS[key] || PORTALS.stay;
